import type { Router } from 'vue-router'

const MISSING_PARAM = 'Missing required param'

/**
 * Makes `router.resolve()` non-throwing for route targets that can no longer be
 * resolved. Returns the same router, for convenience at the call site.
 *
 * Why this exists (VH-2283, Rollbar client #1303/#1311):
 *
 * Vue Router's `useLink` builds `route = computed(() => router.resolve(props.to))`,
 * and `resolve()` reads `currentRoute` — so every mounted link is a reactive
 * subscriber of the current route. Vuetify's link components then pull that computed
 * through a `flush: 'sync'` watcher (`destructComputed` in vuetify/lib/util/helpers),
 * whose dirty-check runs *inside* the `currentRoute.value = ...` assignment in
 * vue-router's `finalizeNavigation` — and is not wrapped in Vue's
 * `callWithErrorHandling`.
 *
 * Consequence: when a provider user logs out, every still-mounted provider-scoped
 * link re-resolves against `/login` before Vue can flush, unmount, or evaluate a
 * single `v-if`. Any target whose `provider_uuid` is absent — including one that
 * simply omits it, since vue-router then inherits the param from the outgoing route
 * — throws `Missing required param "provider_uuid"` straight out of
 * `router.push('/login')`. No guard on the producing side can run early enough, so we
 * degrade the doomed resolution instead; the component that owns the link is being
 * torn down anyway.
 *
 * Safe by construction: the fallback resolves by *path*, which never stringifies
 * required params, and vue-router's internal navigation uses its own closure-scoped
 * `resolve` rather than this property, so guards, redirects and push/replace
 * semantics are untouched.
 */
export function installSafeResolve(router: Router): Router {
  const baseResolve = router.resolve.bind(router) as Router['resolve']

  router.resolve = ((to: Parameters<Router['resolve']>[0], currentLocation?: Parameters<Router['resolve']>[1]) => {
    try {
      return baseResolve(to, currentLocation)
    } catch (error) {
      if (!(error instanceof Error) || !error.message.includes(MISSING_PARAM))
        throw error

      // Deliberately not silent: a link hitting this outside of a teardown is a
      // real bug — a route target built without the params its destination needs.
      console.warn(`[router] ${error.message} — falling back to the current location.`, to)

      return baseResolve({ path: router.currentRoute.value.path })
    }
  }) as Router['resolve']

  return router
}
